// ── The instrument canvas: the approved chart language (28 Aug) ────────────
// Spec: claude.ai/code/artifact/12558e54-6d83-4a3a-b7de-abf8a3f79d62
// Every chart mounts on a CANVAS panel set into the light card - warm paper
// by default, obsidian when the app runs dark. Rules the user set: CLEAR
// TEXT and NO GHOSTING - zero blur/glow filters, no dashed ghost series,
// high-contrast labels; depth comes from gradients and color only.
import React from 'react';
import { InfoTip } from '@/components/ui/info-tip';
import { cn } from '@/lib/utils';

// One theme object per finish. The hue identities match across finishes -
// light uses deep saturated inks for paper, dark uses luminous ones for
// obsidian.
export const CHART_THEMES = {
  light: {
    amber: '#C58E18', orange: '#EDBD48', cyan: '#7FB0F5', blue: '#2D63D8',
    magenta: '#E9766D', green: '#16865E', violet: '#765BC7', red: '#D95F57',
    other: '#8FA8A0', lbl: '#76837E', txt: '#183731',
    grid: '#E9EEEB', gridV: 'rgba(16,46,40,.04)',
    trackBg: 'rgba(16,46,40,.06)', crosshair: 'rgba(16,46,40,.22)',
    dotRing: '#FFFFFF', closeDot: '#183731',
    areaTop: .16, stackTop: 1, stackBot: 1, vesselFill: .35,
    series: ['#2D63D8', '#7FB0F5', '#765BC7', '#EDBD48', '#E9766D', '#8FA8A0'],
  },
  dark: {
    amber: '#EDBD48', orange: '#F1CC70', cyan: '#9CC3F7', blue: '#6D9BEA',
    magenta: '#EE948D', green: '#51BB97', violet: '#947FD3', red: '#E9766D',
    other: '#7E8A86', lbl: '#99A39F', txt: '#E8ECEA',
    grid: '#24382F', gridV: 'rgba(232,236,234,.05)',
    trackBg: 'rgba(232,236,234,.08)', crosshair: 'rgba(232,236,234,.25)',
    dotRing: '#142420', closeDot: '#E8ECEA',
    areaTop: .26, stackTop: 1, stackBot: 1, vesselFill: .45,
    series: ['#6D9BEA', '#9CC3F7', '#947FD3', '#F1CC70', '#EE948D', '#7E8A86'],
  },
};

// The app toggles dark mode with a `dark` class on <html>. Charts re-render
// on data changes anyway; a storage/attribute listener keeps them honest
// when only the theme flips.
export function useChartTheme() {
  const read = () => (typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light');
  const [mode, setMode] = React.useState(read);
  React.useEffect(() => {
    const obs = new MutationObserver(() => setMode(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return CHART_THEMES[mode];
}

// The panel itself. Styling lives in App.css (.g-canvas / .dark .g-canvas)
// so the gradients read from one place.
export function GChartCanvas({ className, children }) {
  return <div className={cn('g-canvas relative overflow-hidden rounded-lg', className)}>{children}</div>;
}

// Catmull-Rom -> cubic bezier: the crisp smooth curve every line in the
// language draws with. pts = [[x,y], ...].
export function smoothPath(pts) {
  if (!pts.length) return '';
  if (pts.length < 3) return pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0]} ${p[1]}`).join(' ');
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
    d += ` C ${p1[0] + (p2[0] - p0[0]) / 6} ${p1[1] + (p2[1] - p0[1]) / 6}, ${p2[0] - (p3[0] - p1[0]) / 6} ${p2[1] - (p3[1] - p1[1]) / 6}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

// Chip legend: each series carries its own total, so the legend answers the
// first question before the tooltip is needed.
export function GChipLegend({ items }) {
  if (!items?.length) return null;
  return (
    // v3: a quiet key, not a row of bordered pills (pastel pills on everything
    // was one of the tells the owner banned on 7 Oct 2026).
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground">
          <span className="size-[8px] rounded-full" style={{ background: it.color }} />
          {it.label}
          {it.value != null && <span className="font-data font-semibold tabular-nums text-muted-foreground">{it.value}</span>}
        </span>
      ))}
    </div>
  );
}

// ── GFunnelChart: the left-aligned vessel funnel ───────────────────────────
// Vessels flush left so the stream visibly narrows on the RIGHT - the leak
// is the missing width. The conversion between stages is printed at the
// narrowing point; the worst step carries the warning color; the final
// stage burns amber.
/**
 * A funnel, redrawn - because the old one could not answer the question it
 * exists for.
 *
 * THREE FAULTS, AND THE SIZE WAS THE SYMPTOM OF ONE.
 *
 * 1. It was an SVG on a fixed 1010-unit viewBox stretched to the container.
 *    In a 1,450px card every unit was multiplied by 1.44, so six stages at 52
 *    high with 40 of gap became 780px of screen - the whole page for six
 *    numbers - and every 14px label rendered at 20px. Same defect as the
 *    waterfall: the sizes in the file were never the sizes anyone saw.
 *
 * 2. Bar width was the share of the top stage, floored at 6%. On a real store
 *    the top is 76,420 and the bottom four are 4,873 / 4,432 / 1,844, so they
 *    all drew at the SAME floored stub. A reader could not tell 4,873 from
 *    1,844 - the chart was actively misleading about the half of the funnel
 *    that is worth acting on.
 *
 * 3. A 300px column on the right carried three words.
 *
 * So: HTML, not SVG. One unit is one pixel, text wraps instead of being
 * clipped at the viewBox edge, and it reflows on a narrow screen.
 *
 * The encoding is deliberate. Magnitude stays LINEAR against the top stage -
 * a log scale would flatter a collapsing funnel and this product does not
 * flatter numbers - with a 2px floor so a real value is never invisible, and
 * the floor is small enough that it cannot be mistaken for a quantity. The
 * comparison a reader actually makes lives in the figures beside the bar,
 * which are exact, and in the step conversion between rows, which is the
 * number you act on and therefore the one drawn strongest.
 */
// stepTip(i) -> an optional explanation for the step from stage i to i+1,
// shown behind an (i) when that step passes 100% (a real data shape, not a
// fault, and one sentence of prose under every such funnel was too many).
export function GFunnelChart({ stages, notes = [], fmt = (v) => v.toLocaleString('en-IN'), stepTip }) {
  if (!stages?.length) return null;
  const top = Math.max(1, stages[0].value);
  const convs = stages.slice(1).map((st, i) => (stages[i].value > 0 ? (100 * st.value) / stages[i].value : null));
  const worst = convs.reduce((w, c, i) => (c != null && (w < 0 || c < convs[w]) ? i : w), -1);
  const pct = (v) => (v >= 10 ? Math.round(v) : Math.round(v * 10) / 10);

  return (
    <div className="font-data">
      {stages.map((st, i) => {
        const share = Math.max(0, st.value) / top;
        const last = i === stages.length - 1;
        const conv = i < stages.length - 1 ? convs[i] : null;
        const isWorst = i === worst && conv != null;
        const note = notes[i] ?? (i === 0 ? 'where everyone starts' : null);
        return (
          <React.Fragment key={st.label}>
            <div className="flex items-center gap-3 py-1">
              <div className="w-[104px] shrink-0 text-right sm:w-[132px]">
                <p className="truncate text-[13px] font-semibold leading-tight text-foreground">{st.label}</p>
                {note && <p className="line-clamp-2 text-[12px] leading-tight text-muted-foreground" title={note}>{note}</p>}
              </div>
              {/* The track makes the empty space readable as "what was lost". */}
              <div className="h-7 min-w-0 flex-1 rounded-md bg-surface-subtle">
                {/* Blue is data in this system; green is the brand and reads
                    as "good", which a funnel stage is not. The last stage is
                    amber because it is the outcome, not another step.
                    Clamped at the track: orders can exceed sessions (an app or
                    wallet purchase never loaded a page we could see) and a bar
                    overflowing its own track reads as a rendering fault - the
                    figure beside it still says 167%. */}
                <div
                  className={cn('h-full rounded-md border',
                    last
                      ? 'border-amber-500/50 bg-amber-400/25'
                      : 'border-sky-500/40 bg-sky-500/20')}
                  style={{ width: `max(2px, ${Math.min(100, share * 100).toFixed(2)}%)` }} />
              </div>
              <div className="w-[92px] shrink-0 text-right tabular-nums sm:w-[108px]">
                <p className="text-[14px] font-semibold leading-tight text-foreground">{fmt(st.value)}</p>
                <p className="text-[12px] leading-tight text-muted-foreground">
                  {i === 0 ? 'of the top' : `${pct(share * 100)}% of the top`}
                </p>
              </div>
            </div>
            {conv != null && (
              <div className="flex items-center gap-3 py-0.5">
                <div className="w-[104px] shrink-0 sm:w-[132px]" />
                <p className={cn('flex items-center gap-1 text-[12px] font-semibold leading-tight tabular-nums',
                  isWorst ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>
                  &darr; {pct(conv)}% carried on{isWorst ? ' · the thinnest step' : ''}
                  {conv > 100 && stepTip && stepTip(i) && <InfoTip size={3}>{stepTip(i)}</InfoTip>}
                </p>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── GWaterfall: a move decomposed factor by factor ─────────────────────────
// steps: [{label, value, kind: 'base'|'delta', tip?}] - base bars stand on
// zero, delta bars hang from the running total. tip = {title, rows:[{l,v,
// tone?}], note} renders as a glass readout on hover: the week bars carry
// the dials that made them, the factor bars carry the dial's movement, its
// rupee impact and its share of the whole move.
export function GWaterfall({ steps, fmt = (v) => String(v), height = 280 }) {
  const T = useChartTheme();
  const [hover, setHover] = React.useState(null);
  // THE CHART'S TYPE WAS SCALING WITH THE CARD.
  //
  // The viewBox was a fixed 620 wide and the svg stretched to its container,
  // so on a 1,060px card every unit was multiplied by 1.71 - a label written
  // as 11 landed on screen at 19px, larger than the page's own headings, and
  // it grew again on a wider screen. The sizes in this file were never the
  // sizes anyone saw.
  //
  // The viewBox now tracks the measured width, so one unit is one pixel and
  // fontSize 12 means 12px at every card width. Nothing here feeds back into
  // the container's size: the svg's width is 100% and its height is fixed, so
  // measuring cannot move what is being measured.
  const wrap = React.useRef(null);
  const [w, setW] = React.useState(620);
  React.useLayoutEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => {
      const px = Math.round(e.contentRect.width);
      if (px > 0) setW(px);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!steps?.length) return null;
  const W = w, H = height, P = { l: 56, r: 16, t: 26, b: 46 };
  const seq = []; let run = 0;
  steps.forEach((st, i) => {
    if (st.kind === 'base') { run = i === 0 ? st.value : run; seq.push({ ...st, lo: 0, hi: i === 0 ? st.value : run }); }
    else { seq.push({ ...st, lo: Math.min(run, run + st.value), hi: Math.max(run, run + st.value) }); run += st.value; }
  });
  const maxV = Math.max(...seq.map((b) => b.hi)) * 1.06;
  const iw = W - P.l - P.r, stepX = iw / seq.length, bw = stepX * 0.56;
  const x = (i) => P.l + stepX * i + stepX / 2, y = (v) => H - P.b - (H - P.b - P.t) * (v / (maxV || 1));
  const el = [];
  for (let g = 0; g <= 3; g++) {
    const yy = P.t + (H - P.b - P.t) * g / 3;
    el.push(<line key={`g${g}`} x1={P.l} y1={yy} x2={W - P.r} y2={yy} stroke={T.grid} />);
    el.push(<text key={`gl${g}`} x={P.l - 8} y={yy + 3.5} textAnchor="end" fontSize="12" fill={T.lbl}>{fmt(maxV * (1 - g / 3))}</text>);
  }
  seq.forEach((b, i) => {
    const base = b.kind === 'base', up = !base && b.value > 0;
    const grad = base ? 'gwf-base' : up ? 'gwf-gain' : 'gwf-loss';
    const cap = base ? T.blue : up ? T.green : T.red;
    const yT = y(b.hi), yB = y(b.lo);
    const dim = hover != null && hover !== i;
    el.push(<g key={`bg${i}`} opacity={dim ? 0.45 : 1} style={{ transition: 'opacity .15s' }}>
      <rect x={x(i) - bw / 2} y={yT} width={bw} height={Math.max(3, yB - yT)} rx="4" fill={`url(#${grad})`} />
      <line x1={x(i) - bw / 2} y1={yT} x2={x(i) + bw / 2} y2={yT} stroke={cap} strokeWidth="1.6" />
      <text x={x(i)} y={yT - 8} textAnchor="middle" fontSize="12" fontWeight="700"
        fill={base ? T.txt : cap} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {base ? fmt(b.hi) : `${b.value > 0 ? '+' : '−'}${fmt(Math.abs(b.value))}`}
      </text>
    </g>);
    if (i < seq.length - 1) {
      const yc = y(b.kind === 'base' ? b.hi : (b.value > 0 ? b.hi : b.lo));
      el.push(<line key={`k${i}`} x1={x(i) + bw / 2} y1={yc} x2={x(i + 1) - bw / 2} y2={yc} stroke={T.crosshair} strokeDasharray="3 4" />);
    }
    b.label.split(' ').forEach((wd, k) => el.push(
      <text key={`w${i}-${k}`} x={x(i)} y={H - 30 + k * 14} textAnchor="middle" fontSize="12" fill={T.lbl}>{wd}</text>));
    // one generous hit zone per column - hovering anywhere in the lane works
    el.push(<rect key={`h${i}`} x={P.l + stepX * i} y={P.t} width={stepX} height={H - P.t - P.b}
      fill="transparent" style={{ cursor: b.tip ? 'pointer' : 'default' }}
      onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />);
  });
  const hb = hover != null ? seq[hover] : null;
  const tipLeft = hb ? Math.min(82, Math.max(18, (x(hover) / W) * 100)) : 0;
  return (
    <div className="relative" ref={wrap}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="block font-data">
        <defs>
          <linearGradient id="gwf-base" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor={T.blue} stopOpacity=".3" /><stop offset="1" stopColor={T.blue} stopOpacity=".85" /></linearGradient>
          <linearGradient id="gwf-gain" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor={T.green} stopOpacity=".35" /><stop offset="1" stopColor={T.green} stopOpacity=".85" /></linearGradient>
          <linearGradient id="gwf-loss" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor={T.red} stopOpacity=".35" /><stop offset="1" stopColor={T.red} stopOpacity=".85" /></linearGradient>
        </defs>
        {el}
      </svg>
      {hb?.tip && (
        <div className="pointer-events-none absolute top-2 z-10 w-[240px] -translate-x-1/2 rounded-lg border border-border bg-popover px-4 py-2 shadow-card"
          style={{ left: `${tipLeft}%` }}>
          <p className="text-[13px] font-medium first-letter:uppercase text-muted-foreground">{hb.tip.title}</p>
          <div className="mt-1.5 space-y-1">
            {hb.tip.rows.map((r) => (
              <div key={r.l} className="flex items-baseline justify-between gap-3 text-[14px]">
                <span className="text-muted-foreground">{r.l}</span>
                <span className={cn('font-data font-semibold tabular-nums',
                  r.tone === 'bad' ? 'text-rose-600 dark:text-rose-400'
                    : r.tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground')}>{r.v}</span>
              </div>
            ))}
          </div>
          {hb.tip.note && <p className="mt-1.5 border-t border-border pt-1.5 text-[14px] leading-snug text-muted-foreground">{hb.tip.note}</p>}
        </div>
      )}
    </div>
  );
}

// ── GHeatmap: weekday x week intensity grid ────────────────────────────────
// rows: [{day: 'YYYY-MM-DD', value}] - the month's rhythm in one glance.
// Weeks run left to right, weekdays top to bottom; the best day is ringed
// and labeled.
export function GHeatmap({ rows, fmt = (v) => String(v), accent }) {
  const T = useChartTheme();
  const color = accent || T.blue;
  const data = (rows || []).filter((r) => r.day && Number.isFinite(Number(r.value)));
  if (data.length < 7) return null;
  const first = new Date(`${data[0].day}T00:00:00`);
  const mon = new Date(first); mon.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  const cellOf = (d) => { const t = new Date(`${d}T00:00:00`); const days = Math.round((t - mon) / 86400000); return { w: Math.floor(days / 7), d: (t.getDay() + 6) % 7 }; };
  const weeks = Math.max(...data.map((r) => cellOf(r.day).w)) + 1;
  const maxV = Math.max(...data.map((r) => Number(r.value)));
  let best = data[0];
  data.forEach((r) => { if (Number(r.value) > Number(best.value)) best = r; });
  const byCell = new Map(data.map((r) => [`${cellOf(r.day).w}:${cellOf(r.day).d}`, r]));
  // Cells used to be 151 x 20 - a 7.5:1 sliver that read as a row of bars, and
  // the whole grid took up a fifth of the card it sits in. Rows now carry real
  // height, so the month reads as a month and every cell's figure is legible.
  // The key sits under the grid, so the grid takes the full width.
  const W = 1010, P = { l: 72, t: 34 }, rowH = 78, ch = rowH - 9;
  const colW = (W - P.l + 8) / weeks;
  const H = P.t + 7 * rowH + 34;
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const el = [];
  dayNames.forEach((n, d) => el.push(<text key={`d${n}`} x={P.l - 14} y={P.t + d * rowH + ch / 2 + 4} textAnchor="end" fontSize="12.5" fill={T.lbl}>{n}</text>));
  for (let w = 0; w < weeks; w++) el.push(<text key={`w${w}`} x={P.l + w * colW + (colW - 8) / 2} y={P.t - 12} textAnchor="middle" fontSize="12.5" fill={T.lbl}>{`Wk ${w + 1}`}</text>);
  // Every day prints its own number - a heatmap that only labels one cell
  // reads as "one day's report". Days outside the window draw as faint
  // empty cells so the grid always reads complete.
  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < 7; d++) {
      const r = byCell.get(`${w}:${d}`);
      const xx = P.l + w * colW, yy = P.t + d * rowH;
      if (!r) {
        el.push(<rect key={`e${w}-${d}`} x={xx} y={yy} width={colW - 8} height={ch} rx="7" fill={T.trackBg} />);
        continue;
      }
      const frac = Math.min(1, Number(r.value) / (maxV || 1));
      const op = .12 + .88 * frac;
      const isBest = r === best;
      el.push(<rect key={r.day} x={xx} y={yy} width={colW - 8} height={ch} rx="7"
        fill={color} fillOpacity={op.toFixed(2)}
        stroke={isBest ? T.txt : 'none'} strokeWidth={isBest ? 2 : 0}>
        <title>{`${r.day} · ${fmt(Number(r.value))}`}</title>
      </rect>);
      if (colW > 64) {
        // ink flips with the cell: dark text on pale cells, white on deep ones
        const inkOnCell = op > .55 ? '#FFFFFF' : (T === CHART_THEMES.dark ? T.txt : '#5C4326');
        el.push(<text key={`t${r.day}`} x={xx + (colW - 8) / 2} y={yy + ch / 2 + 5}
          textAnchor="middle" fontSize="14" fontWeight={isBest ? 800 : 600} fill={inkOnCell}
          style={{ fontVariantNumeric: 'tabular-nums' }} pointerEvents="none">{fmt(Number(r.value))}</text>);
      }
    }
  }
  // The key sits under the grid, right-aligned to its edge. Beside the first
  // row its "less" label ran into the last week's cells.
  const ly = P.t + 7 * rowH + 6, lx = W - 4 - 32 - 81;
  el.push(<text key="less" x={lx - 8} y={ly + 10} textAnchor="end" fontSize="12" fill={T.lbl}>less</text>);
  for (let i = 0; i < 5; i++) el.push(<rect key={`lg${i}`} x={lx + i * 17} y={ly} width="13" height="13" rx="3.5" fill={color} fillOpacity={(.12 + i * .22).toFixed(2)} />);
  el.push(<text key="more" x={W} y={ly + 10} textAnchor="end" fontSize="12" fill={T.lbl}>more</text>);
  return <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full font-data">{el}</svg>;
}
